import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface InfoTipProps {
  /** Localized explanation. When empty, children render as-is (no affordance). */
  tip?: string;
  children: React.ReactNode;
  /** Extra classes on the inline wrapper (e.g. justify-end for right-aligned). */
  className?: string;
}

/**
 * Inline label with a styled hover tooltip. The tooltip is rendered in a portal
 * on document.body and positioned fixed to the viewport, so it is never clipped
 * by the table's `overflow-x-auto` scroll container (which the native title /
 * an in-flow absolute element would be). Shows immediately on hover.
 */
export const InfoTip: React.FC<InfoTipProps> = ({ tip, children, className = '' }) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  if (!tip) return <>{children}</>;

  const show = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const x = Math.min(Math.max(r.left + r.width / 2, 130), window.innerWidth - 130);
    setPos({ x, y: r.bottom + 6 });
  };

  return (
    <span
      className={`relative inline-flex items-center gap-1 cursor-help ${className}`}
      onMouseEnter={(e) => show(e.currentTarget)}
      onMouseLeave={() => setPos(null)}
      onFocus={(e) => show(e.currentTarget)}
      onBlur={() => setPos(null)}
      tabIndex={0}
    >
      <span className="border-b border-dotted border-slate-400/50">{children}</span>
      <Info className="h-3 w-3 shrink-0 opacity-40" aria-hidden />
      {pos &&
        createPortal(
          <span
            role="tooltip"
            style={{ position: 'fixed', left: pos.x, top: pos.y, transform: 'translateX(-50%)' }}
            className="pointer-events-none z-[9999] block max-w-xs rounded-md bg-slate-900 px-3 py-2 text-xs font-normal normal-case leading-snug tracking-normal text-white shadow-lg dark:bg-slate-700"
          >
            {tip}
          </span>,
          document.body,
        )}
    </span>
  );
};
